/**
=========================================================
* Soft UI Dashboard React - v4.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";

// Soft UI Dashboard React base styles
import typography from "assets/theme/base/typography";

// Dashboard layout components
import BuildByDevelopers from "layouts/dashboard/components/BuildByDevelopers";
import WorkWithTheRockets from "layouts/dashboard/components/WorkWithTheRockets";
import Projects from "layouts/dashboard/components/Projects";
import OrderOverview from "layouts/dashboard/components/OrderOverview";

// Data
import reportsBarChartData from "layouts/dashboard/data/reportsBarChartData";
import gradientLineChartData from "layouts/dashboard/data/gradientLineChartData";
import { useEffect, useState } from "react";

const apiUrl = "https://api.getverceipt.com/api/v1";

function Dashboard() {
  const { size } = typography;
  const { chart, items } = reportsBarChartData;

  const [carbon, setCarbon] = useState(0.0);
  const [receipts, setReceipts] = useState(0);
  const [revenue, setRevenue] = useState(0.0);
  const [stores, setStores] = useState(0);

  const [carbonChange, setCarbonChange] = useState(0.0);
  const [receiptsChange, setReceiptsChange] = useState(0.0);
  const [revenueChange, setRevenueChange] = useState(0.0);
  const [storesChange, setStoresChange] = useState(0.0);

  function getCurrentMonthShort() {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", 
                    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const now = new Date();
    return months[now.getMonth()];
  };

  const [month, setMonth] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${apiUrl}/dashboard/analytics/widgets`, {
          method: 'GET',
          withCredentials: true,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log(data);

          setCarbon(data?.carbonFootprint?.totalEmissions || 0.0);
          setReceipts(data?.receipts?.count || 0);
          setRevenue(data?.revenue?.amount || 0);
          setStores(data?.stores?.count || 0);

          setCarbonChange(data?.carbonFootprint?.change || 0.0);
          setReceiptsChange(data?.receipts?.change || 0.0);
          setRevenueChange(data?.revenue?.change || 0.0);
          setStoresChange(data?.stores?.change || 0.0);
        }
      } catch (err) {
        console.error(err);
      }
    }

    fetchData();
    setMonth(getCurrentMonthShort());
  }, []);
  
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                bgColor="info"
                title={{ text: `receipts (${month})` }}
                count={receipts}
                percentage={{
                  color: receiptsChange > 0 ? "success" : receiptsChange < 0 ? "error" : "text",
                  text: receiptsChange == 0 ? "" : `${receiptsChange > 0 ? "+" : ""}${receiptsChange}%`,
                }}
                icon={{ color: "info", component: "receiptlong" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                bgColor="success"
                title={{ text: `Carbon Footprint (${month})` }}
                count={`${carbon} kg CO₂`}
                percentage={{
                  color: carbonChange > 0 ? "success" : carbonChange < 0 ? "error" : "text",
                  text: carbonChange == 0 ? "" : `${carbonChange > 0 ? "+" : ""}${carbonChange}%`,
                }}
                icon={{ color: "info", component: "public" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                bgColor="primary"
                title={{ text: `Revenue (${month})` }}
                count={`£${revenue}`}
                percentage={{
                  color: revenueChange > 0 ? "success" : revenueChange < 0 ? "error" : "text",
                  text: revenueChange == 0 ? "" : `${revenueChange > 0 ? "+" : ""}${revenueChange}%`,
                }}
                icon={{ color: "info", component: "paid" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                bgColor="dark"
                title={{ text: "Stores" }}
                count={stores}
                percentage={{
                  color: storesChange > 0 ? "success" : storesChange < 0 ? "error" : "text",
                  text: storesChange == 0 ? "" : `${storesChange > 0 ? "+" : ""}${storesChange}%`,
                }}
                icon={{
                  color: "info",
                  component: "store",
                }}
              />
            </Grid>
          </Grid>
        </SoftBox>
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <BuildByDevelopers />
            </Grid>
            <Grid item xs={12} lg={5}>
              <WorkWithTheRockets />
            </Grid>
          </Grid>
        </SoftBox>
        {/*
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={5}>
              <ReportsBarChart
                title="active users"
                description={
                  <>
                    (<strong>+23%</strong>) than last week
                  </>
                }
                chart={chart}
                items={items}
              />
            </Grid>
            <Grid item xs={12} lg={7}>
              <GradientLineChart
                title="Sales Overview"
                description={
                  <SoftBox display="flex" alignItems="center">
                    <SoftBox fontSize={size.lg} color="success" mb={0.3} mr={0.5} lineHeight={0}>
                      <Icon className="font-bold">arrow_upward</Icon>
                    </SoftBox>
                    <SoftTypography variant="button" color="text" fontWeight="medium">
                      4% more{" "}
                      <SoftTypography variant="button" color="text" fontWeight="regular">
                        in 2021
                      </SoftTypography>
                    </SoftTypography>
                  </SoftBox>
                }
                height="20.25rem"
                chart={gradientLineChartData}
              />
            </Grid>
          </Grid>
        </SoftBox>*/}
        {/*
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={8}>
            <Projects />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <OrderOverview />
          </Grid>
        </Grid>*/}
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
